import { __decorate, __metadata } from "tslib";
import { asyncTimeout } from '@1inch-community/core/async';
import { lazyAppContextConsumer } from '@1inch-community/core/lazy';
import { appendClass, observe, translate } from '@1inch-community/core/lit-utils';
import '@1inch-community/ui-components/scroll';
import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { map as LitMap } from 'lit/directives/map.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { BehaviorSubject, combineLatest, debounceTime, defer, map, merge, shareReplay, startWith, switchMap, tap, } from 'rxjs';
import { changeSearchState } from '../events';
import '../token-item-cross-chain-accordion';
import '../token-item-cross-chain-flat';
import '../token-item-loader';
import { tokenListStyle } from './token-list.style';
let TokenListElement = class TokenListElement extends LitElement {
    constructor() {
        super(...arguments);
        this.showFavoriteTokenToggle = false;
        this.showOnlyWithBalance = false;
        this.mobileView = false;
        this.searchInProgress = false;
        this.expandedAccordionItemIndex = null;
        this.isEmpty = false;
        this.emptyList = new Array(50).fill('loader');
        this.tokenListViewDataSnapshot = null;
        this.applicationContext = lazyAppContextConsumer(this);
        this.virtualizedRef = createRef();
        this.type$ = new BehaviorSubject('accordion');
        this.walletAddress$ = new BehaviorSubject(undefined);
        this.chainIds$ = new BehaviorSubject([]);
        this.searchFilter$ = new BehaviorSubject('');
        this.crossChainEmitter$ = defer(() => this.applicationContext.value.onChain.crossChainEmitter).pipe(startWith(null));
        this.tokensUpdate$ = defer(() => this.applicationContext.value.tokenStorage.tokensUpdate$).pipe(startWith(null));
        this.balancesUpdate$ = defer(() => this.applicationContext.value.tokenStorage.balancesUpdate$).pipe(startWith(null));
        this.tokenListByViewTypeAndSearchFilterAndChainIds$ = combineLatest([
            this.type$,
            this.searchFilter$,
            this.chainIds$,
            this.walletAddress$,
            merge(this.crossChainEmitter$, this.tokensUpdate$, this.balancesUpdate$),
        ]).pipe(debounceTime(0), switchMap(([type, searchFilter, chainIds, walletAddress]) => {
            if (type === 'flat' || searchFilter.length > 0) {
                return this.applicationContext.value.tokenStorage.getTokenIdList({
                    chainIds,
                    tokensOnlyWithBalance: this.showOnlyWithBalance,
                    walletAddress: walletAddress || null,
                    tokenNameSymbolAddressMatches: searchFilter || null,
                });
            }
            if (type === 'accordion') {
                return this.applicationContext.value.tokenStorage.getSymbolData({
                    chainIds,
                    tokensOnlyWithBalance: this.showOnlyWithBalance,
                    walletAddress: walletAddress || null,
                });
            }
            throw new Error(`TokenListElementError: Unknown type: ${type}`);
        }));
        this.dataIndexListOfFlatTokenIdList$ = this.tokenListByViewTypeAndSearchFilterAndChainIds$.pipe(map((data) => {
            if (Array.isArray(data)) {
                this.tokenListViewDataSnapshot = null;
                return data;
            }
            const length = data.userTokensInfo.length + data.allTokensInfo.length;
            this.tokenListViewDataSnapshot = data;
            return new Array(length).fill(0);
        }), tap((data) => {
            if (this.searchInProgress) {
                this.searchInProgress = false;
                changeSearchState(this, this.searchInProgress);
            }
            this.isEmpty = data.length === 0;
        }), shareReplay({ bufferSize: 1, refCount: true }));
    }
    static { this.tagName = 'inch-token-list'; }
    static { this.styles = tokenListStyle; }
    set type(value) {
        this.type$.next(value);
    }
    set chainIds(value) {
        if (!Array.isArray(value))
            return;
        this.chainIds$.next(value);
    }
    set walletAddress(value) {
        if (!value)
            return;
        this.walletAddress$.next(value);
    }
    set searchFilter(value) {
        if (this.searchFilter$.value === value)
            return;
        this.searchFilter$.next(value);
        this.requestUpdate();
        if (!this.searchInProgress) {
            this.searchInProgress = true;
            changeSearchState(this, this.searchInProgress);
        }
    }
    render() {
        appendClass(this, {
            empty: this.isEmpty,
            search: this.searchFilter$.value.length > 0,
        });
        return html `
      ${this.renderEmptySearchView()}
      <inch-scroll-view-virtualizer-consumer
        ${ref(this.virtualizedRef)}
        .header="${this.header}"
        .stubView=${() => this.renderStubList()}
        .items=${observe(this.dataIndexListOfFlatTokenIdList$, this.emptyList)}
        .keyFunction="${(id, index) => this.keyFunction(id, index)}"
        .renderItem="${(id, index) => this.renderItem(id, index)}"
      ></inch-scroll-view-virtualizer-consumer>
    `;
    }
    renderEmptySearchView() {
        return html `
      <div class="empty-search">
        <inch-icon icon="emptySearch"></inch-icon>
        <h3>${translate('inch-token-list.search-empty', { name: this.searchFilter$.value })}</h3>
        <span>${translate('inch-token-list.search-empty-description')}</span>
      </div>
    `;
    }
    renderItem(id, index) {
        if (id === 'loader') {
            return this.renderLoaderItem(index);
        }
        if (id === 0) {
            return this.renderAccordionItem(index);
        }
        if (typeof id === 'string') {
            return this.renderFlatItem(id, index);
        }
        throw new Error(`TokenListElementError: Unsupported template`);
    }
    renderStubList() {
        return html `${LitMap(this.emptyList, (_, index) => this.renderLoaderItem(index))}`;
    }
    renderLoaderItem(index) {
        return html `
      <inch-token-item-loader
        .iconAfterStyle="${this.type$.value === 'accordion'
            ? { transform: 'rotate(-90deg)' }
            : undefined}"
        .showIconAfter="${this.type$.value === 'accordion'}"
        .index="${index}"
        .iconAfterName="${this.type$.value === 'accordion' ? 'chevronDown16' : undefined}"
        .mobileView="${this.mobileView}"
      ></inch-token-item-loader>
    `;
    }
    renderAccordionItem(index) {
        const record = this.extractTokenViewDataByIndex(index);
        const walletAddress = this.walletAddress$.value;
        const chainIds = this.chainIds$.value;
        return html `<inch-token-item-cross-chain-accordion
      .favoriteTokenIds="${this.favoriteTokenIds}"
      .showFavoriteTokenToggle="${this.showFavoriteTokenToggle}"
      .crossChainTokensBindingRecord="${record}"
      .walletAddress="${walletAddress}"
      .showChainIds="${chainIds}"
      .expanded="${this.expandedAccordionItemIndex === index}"
      .index="${index}"
      .mobileView="${this.mobileView}"
      @changeExpand="${() => this.changeExpandHandler(index)}"
    ></inch-token-item-cross-chain-accordion>`;
    }
    renderFlatItem(id, index) {
        const walletAddress = this.walletAddress$.value;
        return html ` <inch-token-item-cross-chain-flat
      .showFavoriteTokenToggle="${this.showFavoriteTokenToggle}"
      .tokenId="${id}"
      .walletAddress="${walletAddress}"
      .index="${index}"
      .mobileView="${this.mobileView}"
      .isFavorite="${this.favoriteTokenIds?.includes(id)}"
    ></inch-token-item-cross-chain-flat>`;
    }
    keyFunction(id, index) {
        if (id === 0 && this.tokenListViewDataSnapshot !== null) {
            const record = this.extractTokenViewDataByIndex(index);
            return [record.symbol, ...record.tokenRecordIds].join('');
        }
        if (typeof id === 'string' && id !== 'loader') {
            return id;
        }
        if (id !== 'loader') {
            return `loader-${index}`;
        }
        return index.toString();
    }
    extractTokenViewDataByIndex(index) {
        if (this.tokenListViewDataSnapshot === null)
            return null;
        if (index >= this.tokenListViewDataSnapshot.userTokensInfo.length) {
            const allTokensInfoIndex = index - this.tokenListViewDataSnapshot.userTokensInfo.length;
            return this.tokenListViewDataSnapshot.allTokensInfo[allTokensInfoIndex] ?? null;
        }
        return this.tokenListViewDataSnapshot.userTokensInfo[index];
    }
    async changeExpandHandler(index) {
        if (this.expandedAccordionItemIndex === index) {
            this.expandedAccordionItemIndex = null;
        }
        else {
            this.expandedAccordionItemIndex = index;
            await asyncTimeout(300);
            this.virtualizedRef.value?.scrollToIndex(index);
        }
    }
};
__decorate([
    property({ type: Boolean, attribute: true }),
    __metadata("design:type", Object)
], TokenListElement.prototype, "showFavoriteTokenToggle", void 0);
__decorate([
    property({ type: Boolean, attribute: true }),
    __metadata("design:type", Object)
], TokenListElement.prototype, "showOnlyWithBalance", void 0);
__decorate([
    property({ type: Boolean, attribute: false }),
    __metadata("design:type", Object)
], TokenListElement.prototype, "mobileView", void 0);
__decorate([
    property({ type: Array, attribute: false }),
    __metadata("design:type", Array)
], TokenListElement.prototype, "favoriteTokenIds", void 0);
__decorate([
    property({ type: Function, attribute: false }),
    __metadata("design:type", Function)
], TokenListElement.prototype, "header", void 0);
__decorate([
    property({ type: String, attribute: true }),
    __metadata("design:type", String),
    __metadata("design:paramtypes", [String])
], TokenListElement.prototype, "type", null);
__decorate([
    property({ type: Array, attribute: false }),
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [Array])
], TokenListElement.prototype, "chainIds", null);
__decorate([
    property({ type: String, attribute: false }),
    __metadata("design:type", String),
    __metadata("design:paramtypes", [String])
], TokenListElement.prototype, "walletAddress", null);
__decorate([
    property({ type: String, attribute: false }),
    __metadata("design:type", String),
    __metadata("design:paramtypes", [String])
], TokenListElement.prototype, "searchFilter", null);
__decorate([
    state(),
    __metadata("design:type", Object)
], TokenListElement.prototype, "expandedAccordionItemIndex", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], TokenListElement.prototype, "isEmpty", void 0);
TokenListElement = __decorate([
    customElement(TokenListElement.tagName)
], TokenListElement);
export { TokenListElement };
//# sourceMappingURL=token-list.element.js.map